import React, { useState, useMemo } from 'react';
import {
  Download, FileSpreadsheet, DollarSign, Calendar, ShoppingBag,
  Filter, Search, TrendingUp, CheckCircle2, Clock, ArrowUpRight
} from 'lucide-react';
import { calculateSaldoPendiente } from '../utils/depositBalance';
import { orderView, isPaidOrder } from '../utils/orders';

// Los pedidos del bot guardan sus ítems como { nombre, cantidad, precio }; los manuales y antiguos pueden traer { name, qty }.
const itemLabel = (i) => `${i.cantidad ?? i.qty ?? 1}x ${i.nombre || i.name || 'Ítem'}`;

export default function FinancialReports({ activeBusiness }) {
  const [filterType, setFilterType] = useState('all'); // 'all' | 'paid' | 'pending'
  const [searchTerm, setSearchTerm] = useState('');

  // 1. Unificar citas y pedidos en un solo flujo transaccional
  const transactions = useMemo(() => {
    const list = [];
    
    // Citas
    if (activeBusiness?.appointments) {
      activeBusiness.appointments.forEach(a => {
        const price = Number(a.precio_cobrado || a.price || 0);
        const saldoPendiente = calculateSaldoPendiente(price, a.deposit_amount, a.deposit_status);
        const paymentLabel = (() => {
          if (a.estado_pago === 'Pagado' || a.status === 'Completada') return 'Pagado completo';
          if (a.deposit_status === 'confirmed' && saldoPendiente <= 0) return 'Pagado completo';
          if (a.deposit_status === 'confirmed') return 'Anticipo confirmado';
          if (a.deposit_status === 'declared') return 'Anticipo declarado';
          return 'Sin pago';
        })();
        list.push({
          id: `cita-${a.id}`,
          date: a.date || a.fecha || 'Sin fecha',
          time: a.time || a.hora || '',
          type: 'Cita',
          customerName: a.clientName || a.cliente || 'Cliente',
          customerPhone: a.phone || a.telefono || 'N/A',
          detail: a.serviceName || a.servicio || 'Servicio General',
          staff: a.staffAssigned || a.especialista || 'Asignado',
          status: a.status || 'Pendiente',
          paymentStatus: paymentLabel,
          amount: price,
          saldoPendiente: paymentLabel === 'Pagado completo' ? 0 : saldoPendiente
        });
      });
    }

    // Pedidos
    if (activeBusiness?.orders) {
      activeBusiness.orders.forEach(o => {
        const view = orderView(o);
        const total = view.total;
        const saldoPendiente = calculateSaldoPendiente(total, o.deposit_amount, o.deposit_status);
        const paymentLabel = (() => {
          if (view.paid || isPaidOrder(o)) return 'Pagado completo';
          if (o.deposit_status === 'confirmed' && saldoPendiente <= 0) return 'Pagado completo';
          if (o.deposit_status === 'confirmed') return 'Anticipo confirmado';
          if (o.deposit_status === 'declared') return 'Anticipo declarado';
          return 'Sin pago';
        })();
        let detailText = 'Pedido WhatsApp';
        if (typeof o.items === 'string') {
          try {
            const parsed = JSON.parse(o.items);
            detailText = Array.isArray(parsed) ? parsed.map(itemLabel).join(', ') : o.items;
          } catch (e) {
            detailText = o.items;
          }
        } else if (Array.isArray(o.items)) {
          detailText = o.items.map(itemLabel).join(', ');
        }

        list.push({
          id: `order-${o.id}`,
          date: (o.created_at || '').split('T')[0] || 'Hoy',
          time: (o.created_at || '').split('T')[1]?.slice(0, 5) || '',
          type: 'Pedido',
          customerName: view.name || 'Cliente WhatsApp',
          customerPhone: view.phone || 'N/A',
          detail: detailText,
          staff: o.delivery_staff_id ? 'Repartidor Asignado' : 'Mostrador / Cocina',
          status: o.status === 'delivered' ? 'Entregado' : (o.status || 'En Proceso'),
          paymentStatus: paymentLabel,
          amount: total,
          saldoPendiente: paymentLabel === 'Pagado completo' ? 0 : saldoPendiente
        });
      });
    }

    // Ordenar de más reciente a antiguo (por fecha)
    return list.sort((a, b) => b.date.localeCompare(a.date));
  }, [activeBusiness]);

  // 2. Filtrado interactivo
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      const matchesSearch = t.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            t.detail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            t.staff.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      if (filterType === 'paid') return t.paymentStatus === 'Pagado completo';
      if (filterType === 'pending') return t.paymentStatus !== 'Pagado completo';
      return true;
    });
  }, [transactions, filterType, searchTerm]);

  // 3. Métricas extra para el Ticket Promedio
  const stats = useMemo(() => {
    const paidList = transactions.filter(t => t.paymentStatus === 'Pagado completo');
    const totalPaid = paidList.reduce((sum, t) => sum + t.amount, 0);
    const avgTicket = paidList.length > 0 ? totalPaid / paidList.length : 0;
    
    const apptsTotal = paidList.filter(t => t.type === 'Cita').reduce((sum, t) => sum + t.amount, 0);
    const ordersTotal = paidList.filter(t => t.type === 'Pedido').reduce((sum, t) => sum + t.amount, 0);

    return {
      totalTransactions: transactions.length,
      paidCount: paidList.length,
      avgTicket,
      apptsTotal,
      ordersTotal,
      totalPaid
    };
  }, [transactions]);

  // 4. Exportador CSV compatible con Excel (\uFEFF BOM para acentos en español)
  const handleExportCSV = () => {
    if (transactions.length === 0) {
      alert("No hay transacciones registradas para exportar.");
      return;
    }

    const headers = [
      "ID", "Fecha", "Hora", "Tipo", "Cliente", "Teléfono", 
      "Detalle / Servicio", "Responsable", "Estado Servicio", "Estado Pago", "Total ($)"
    ];

    const rows = filteredTransactions.map(t => [
      t.id,
      t.date,
      t.time,
      t.type,
      `"${t.customerName.replace(/"/g, '""')}"`,
      `"${t.customerPhone}"`,
      `"${t.detail.replace(/"/g, '""')}"`,
      `"${t.staff.replace(/"/g, '""')}"`,
      t.status,
      t.paymentStatus,
      t.amount
    ]);

    const csvContent = "\uFEFF" + [
      headers.join(","),
      ...rows.map(r => r.join(","))
    ].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const fileName = `Reporte_Keyla_CRM_${(activeBusiness?.nombre_negocio || 'Negocio').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 mt-8">
      {/* Cabecera de Reportes Avanzados */}
      <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="font-extrabold text-lg text-seryi-heading flex items-center gap-2">
            <FileSpreadsheet className="text-seryi-green" size={22} />
            Libro de Movimientos Contables y Exportación
          </h3>
          <p className="text-xs text-seryi-text mt-0.5">
            Descarga en Excel/CSV el registro de todos tus ingresos por citas y pedidos para control contable.
          </p>
        </div>
        
        <button
          onClick={handleExportCSV}
          className="bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark font-bold px-5 py-3 rounded-2xl text-xs flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 shrink-0"
        >
          <Download size={16} />
          Exportar Reporte (.CSV)
        </button>
      </div>

      {/* Ticket Promedio y Distribución */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-2xl space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Ticket Promedio</span>
            <TrendingUp size={16} className="text-emerald-400" />
          </div>
          <p className="text-2xl font-black text-seryi-heading">
            ${stats.avgTicket.toLocaleString('es-CO', { maximumFractionDigits: 0 })}
          </p>
          <p className="text-[10px] text-seryi-text">Promedio por transacción pagada</p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-2xl space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Ingresos por Citas</span>
            <Calendar size={16} className="text-blue-400" />
          </div>
          <p className="text-2xl font-black text-blue-400">
            ${stats.apptsTotal.toLocaleString('es-CO')}
          </p>
          <p className="text-[10px] text-seryi-text font-semibold">
            {stats.totalPaid > 0 ? `${Math.round((stats.apptsTotal / stats.totalPaid) * 100)}% del total` : '0%'}
          </p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-2xl space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Ingresos por Pedidos</span>
            <ShoppingBag size={16} className="text-purple-400" />
          </div>
          <p className="text-2xl font-black text-purple-400">
            ${stats.ordersTotal.toLocaleString('es-CO')}
          </p>
          <p className="text-[10px] text-seryi-text font-semibold">
            {stats.totalPaid > 0 ? `${Math.round((stats.ordersTotal / stats.totalPaid) * 100)}% del total` : '0%'}
          </p>
        </div>
      </div>

      {/* Barra de Filtros y Buscador */}
      <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-3xl space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                filterType === 'all' 
                  ? 'bg-seryi-green text-seryi-bg-dark shadow-md' 
                  : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'
              }`}
            >
              Todos ({transactions.length})
            </button>
            <button
              onClick={() => setFilterType('paid')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                filterType === 'paid' 
                  ? 'bg-emerald-500 text-seryi-bg-dark shadow-md' 
                  : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'
              }`}
            >
              Pagados ({transactions.filter(t => t.paymentStatus === 'Pagado completo').length})
            </button>
            <button
              onClick={() => setFilterType('pending')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                filterType === 'pending' 
                  ? 'bg-amber-500 text-seryi-bg-dark shadow-md' 
                  : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'
              }`}
            >
              Pendientes ({transactions.filter(t => t.paymentStatus !== 'Pagado completo').length})
            </button>
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3.5 top-3 text-seryi-text" size={15} />
            <input
              type="text"
              placeholder="Buscar cliente, ítem..."
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl pl-9 pr-4 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Tabla de Movimientos */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-seryi-bg-light text-[10px] uppercase font-bold text-seryi-text tracking-wider">
                <th className="py-3 px-4">Fecha</th>
                <th className="py-3 px-4">Tipo</th>
                <th className="py-3 px-4">Cliente / Teléfono</th>
                <th className="py-3 px-4">Detalle</th>
                <th className="py-3 px-4">Responsable</th>
                <th className="py-3 px-4">Pago</th>
                <th className="py-3 px-4 text-right">Monto</th>
                <th className="py-3 px-4 text-right">Saldo Pendiente</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-seryi-bg-light/50 text-xs">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-seryi-text italic">
                    No se encontraron transacciones que coincidan con tu búsqueda o filtro.
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((t, idx) => (
                  <tr key={idx} className="hover:bg-seryi-bg-dark/40 transition-colors">
                    <td className="py-3.5 px-4 font-mono text-seryi-text">
                      <div className="font-semibold text-seryi-heading">{t.date}</div>
                      <div className="text-[10px]">{t.time}</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${
                        t.type === 'Cita' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'
                      }`}>
                        {t.type === 'Cita' ? '🗓️ Cita' : '🛍️ Pedido'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-seryi-heading">{t.customerName}</div>
                      <div className="text-[11px] text-seryi-text">{t.customerPhone}</div>
                    </td>
                    <td className="py-3.5 px-4 max-w-xs truncate text-seryi-heading" title={t.detail}>
                      {t.detail}
                    </td>
                    <td className="py-3.5 px-4 text-seryi-text">
                      {t.staff}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${
                        t.paymentStatus === 'Pagado completo'
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                          : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                      }`}>
                        {t.paymentStatus === 'Pagado completo' ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                        {t.paymentStatus}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right font-black text-seryi-heading">
                      ${t.amount.toLocaleString('es-CO')}
                    </td>
                    <td className="py-3.5 px-4 text-right font-bold text-amber-400">
                      {t.saldoPendiente > 0 ? `$${t.saldoPendiente.toLocaleString('es-CO')}` : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
